//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  ListItem, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DnsIcon from '@mui/icons-material/Dns';
import SettingsIcon from '@mui/icons-material/Settings';

import { drawerRowSx } from './drawerRowSx';

interface SettingsDrawerSectionProps {
  isAdmin: boolean;
  // Personal settings — the drawer stands in for the identity menu, so this
  // opens the same personal scope that menu does.
  onOpenSettings: () => void;
  // The server and admin views ride their own header controls on desktop,
  // both absent below `sm`; these rows are where they live on a phone.
  onOpenServerSettings: () => void;
  onOpenAdminSettings: () => void;
}

/**
 * The mobile drawer's settings rows: personal Settings for everyone, the
 * server view for everyone, and Admin for an admin — mirroring the three
 * separate desktop controls rather than unscoping the Settings row, so the
 * scopes a user learns on one viewport hold on the other.
 */
const SettingsDrawerSection = ({
  isAdmin, onOpenSettings, onOpenServerSettings, onOpenAdminSettings
}: SettingsDrawerSectionProps) => {
  const theme = useTheme();
  const rowSx = drawerRowSx(theme);

  const rows = [
    { name: 'Settings', icon: <SettingsIcon />, open: onOpenSettings },
    { name: 'Server', icon: <DnsIcon />, open: onOpenServerSettings },
    ...isAdmin ? [{
      name: 'Admin',
      icon: <AdminPanelSettingsIcon />,
      open: onOpenAdminSettings
    }] : []
  ];

  return (
    <>
      {rows.map((row) => (
        <ListItem key={row.name} disablePadding>
          <ListItemButton onClick={row.open} sx={rowSx}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {row.icon}
            </ListItemIcon>
            <ListItemText primary={row.name} />
          </ListItemButton>
        </ListItem>
      ))}
    </>
  );
};

export default SettingsDrawerSection;
